import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate, historyToString, calculateVolume, formatListItems, buildCombinedReason, getFacilityDisplayName } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';

export function RenalPDF({ data, facilityInfo }) {
  const reason = buildCombinedReason(data.reason, data.history);

  const rk = data.rightKidney || {};
  const lk = data.leftKidney || {};
  const bl = data.bladder || {};

  const rkVol = (rk.size1 && rk.size2 && rk.size3)
    ? calculateVolume(rk.size1, rk.size2, rk.size3) : null;
  const lkVol = (lk.size1 && lk.size2 && lk.size3)
    ? calculateVolume(lk.size1, lk.size2, lk.size3) : null;

  const formatMasses = (masses) => (masses || [])
    .filter(Boolean)
    .map(mass => `${mass.echotexture || 'Mass'}, ${mass.size1} x ${mass.size2} x ${mass.size3} cm`)
    .join('; ');

  const measurementsText = [
    rk.nephrectomy ? 'Right kidney: Nephrectomy' :
    rk.congenitallyAbsent ? 'Right kidney: Congenitally Absent' : [
      rk.size1 && rk.size2 && rk.size3 ? `Right kidney: ${rk.size1} x ${rk.size2} x ${rk.size3} cm` : null,
      rkVol ? `Volume: ${rkVol} cc` : null,
    ].filter(Boolean).join(', '),
    lk.nephrectomy ? 'Left kidney: Nephrectomy' :
    lk.congenitallyAbsent ? 'Left kidney: Congenitally Absent' : [
      lk.size1 && lk.size2 && lk.size3 ? `Left kidney: ${lk.size1} x ${lk.size2} x ${lk.size3} cm` : null,
      lkVol ? `Volume: ${lkVol} cc` : null,
    ].filter(Boolean).join(', '),
    bl.nwv ? 'Bladder: Not well visualized' : [
      bl.prevoid ? `Pre-void volume: ${bl.prevoid} cc` : null,
      bl.postvoid ? `Post-void volume: ${bl.postvoid} cc` : null,
    ].filter(Boolean).join(', '),
  ].filter(Boolean).join('\n');

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="RENAL ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <PdfMeasurementBox title="MEASUREMENTS">
              <Text style={commonStyles.infoBoxText}>{measurementsText}</Text>
            </PdfMeasurementBox>
            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            <PdfAccentSectionHeader title="RIGHT KIDNEY" />
            {rk.nephrectomy ? (
              <PdfLabelValue label="Status:" value="Nephrectomy" forceBlack />
            ) : rk.congenitallyAbsent ? (
              <PdfLabelValue label="Status:" value="Congenitally Absent" forceBlack />
            ) : (
              <>
                <PdfLabelValue label="Echotexture:" value={rk.echotexture || ''} isAbnormal={rk.echotexture && rk.echotexture !== 'Normal'} />
                <PdfLabelValue label="Hydronephrosis:" value={rk.hydronephrosis || 'None'} isAbnormal={rk.hydronephrosis && rk.hydronephrosis !== 'None'} />
                {rk.masses && rk.masses.length > 0 && (
                  <PdfLabelValue label="Masses:" value={formatMasses(rk.masses)} isAbnormal />
                )}
                {rk.other && rk.other.length > 0 && (
                  <PdfLabelValue label="Other:" value={rk.other.filter(Boolean).join(', ')} isAbnormal />
                )}
              </>
            )}

            <PdfAccentSectionHeader title="LEFT KIDNEY" />
            {lk.nephrectomy ? (
              <PdfLabelValue label="Status:" value="Nephrectomy" forceBlack />
            ) : lk.congenitallyAbsent ? (
              <PdfLabelValue label="Status:" value="Congenitally Absent" forceBlack />
            ) : (
              <>
                <PdfLabelValue label="Echotexture:" value={lk.echotexture || ''} isAbnormal={lk.echotexture && lk.echotexture !== 'Normal'} />
                <PdfLabelValue label="Hydronephrosis:" value={lk.hydronephrosis || 'None'} isAbnormal={lk.hydronephrosis && lk.hydronephrosis !== 'None'} />
                {lk.masses && lk.masses.length > 0 && (
                  <PdfLabelValue label="Masses:" value={formatMasses(lk.masses)} isAbnormal />
                )}
                {lk.other && lk.other.length > 0 && (
                  <PdfLabelValue label="Other:" value={lk.other.filter(Boolean).join(', ')} isAbnormal />
                )}
              </>
            )}

            <PdfAccentSectionHeader title="BLADDER" />
            {bl.nwv ? (
              <PdfLabelValue label="Status:" value="Not well visualized" isAbnormal />
            ) : (
              <>
                {bl.noVoid && <PdfLabelValue label="Status:" value="Patient unable to void" forceBlack />}
                <PdfLabelValue label="Right ureteral jet:" value={bl.rightJet ? 'Present' : 'Absent'} isAbnormal={!bl.rightJet} />
                <PdfLabelValue label="Left ureteral jet:" value={bl.leftJet ? 'Present' : 'Absent'} isAbnormal={!bl.leftJet} />
                {bl.other && bl.other.length > 0 && (
                  <PdfLabelValue label="Other:" value={bl.other.filter(Boolean).join(', ')} isAbnormal />
                )}
              </>
            )}
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
